import { HeartIcon } from "lucide-react";
import { Button } from "../ui/button";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useFavoriteUser, useUnfavoriteUser } from "@/tanstack-hooks";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { useHaptics } from "@/hooks/useHaptics";

export function FavoriteButton({
  userId,
  className,
  active = false,
  disabled = false,
}: {
  userId: string;
  className?: string;
  active: boolean;
  disabled?: boolean;
}) {
  const { toast } = useToast();
  const { simpleTrigger, favoritedTrigger } = useHaptics();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isActive, setIsActive] = useState(active);
  const favoriteMutation = useFavoriteUser();
  const unfavoriteMutation = useUnfavoriteUser();
  const t = useTranslation();

  const handleClick = () => {
    if (!isActive) {
      // Optimistically update state
      setIsActive(true);
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 400);
      favoritedTrigger();

      favoriteMutation.mutate(userId, {
        onError: (error) => {
          setIsActive(false);
          console.error("Error - could not favorite user", error);
          toast({
            variant: "destructive",
            title: t.FAVORITES_PAGE.ERRORS.COULD_NOT_FAVORITE,
            description: t.SHARED.ERRORS.PERSISTENT,
            className: "dark",
          });
        },
      });
    } else {
      // Optimistically update state
      setIsActive(false);
      simpleTrigger();

      unfavoriteMutation.mutate(userId, {
        onError: (error) => {
          setIsActive(true);
          console.error("Error - could not favorite user", error);
          toast({
            variant: "destructive",
            title: t.FAVORITES_PAGE.ERRORS.COULD_NOT_UNFAVORITE,
            description: t.SHARED.ERRORS.PERSISTENT,
            className: "dark",
          });
        },
      });
    }
  };

  return (
    <>
      <style>
        {` @keyframes bounceAnimation {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.20); }
            75% { transform: scale(0.90); }
          }
          .animate-bounce-custom {
            animation: bounceAnimation 0.4s ease-in-out;
          }
        `}
      </style>
      <Button variant="link" onClick={handleClick} disabled={disabled}>
        <HeartIcon
          className={cn(
            `h-9 w-9 md:h-7 md:w-7 ${
              isAnimating ? "animate-bounce-custom" : ""
            } ${isActive ? "fill-red-700 stroke-red-700" : "stroke-white"} ${
              disabled ? "fill-muted-foreground stroke-muted-foreground" : ""
            }`,
            className,
          )}
        />
      </Button>
    </>
  );
}
